import { createSelector } from 'reselect';
import {
    filter, find, flatMap, get, has, isFunction, maxBy, minBy, values,
} from 'lodash';
import { buildReservationCruisesByReservationIdSelector } from 'store/selectors/reservationCruises.selector';
import { buildReservationFlightsByReservationIdSelector } from 'store/selectors/reservationFlights.selector';
import { buildReservationHotelRoomsByReservationId } from 'store/selectors/reservationHotelRooms.selector';
import { buildReservationHotelsByReservationIdSelector } from 'store/selectors/reservationHotels.selector';
import { buildReservationInsurancesByReservationIdSelector } from 'store/selectors/reservationInsurances.selector';
import { buildReservationServicesByReservationIdSelector } from 'store/selectors/reservationServices.selector';

const getEntityId = (state, id) => id;
const getEntities = (state) => get(state, 'app.database.reservations', {}); // Use lodash get
const getMetaEntities = (state) => get(state, 'app.metaDatabase.reservations', {});
const getRequests = (state) => get(state, 'app.database.requests', {}); // Use lodash get
const getUsers = (state) => get(state, 'app.database.users', {}); // Use lodash get
const getMembers = (state) => get(state, 'app.database.members', {}); // Use lodash get
const getMemberEmails = (state) => get(state, 'app.database.memberEmails', {}); // Use lodash get
const getReservationAssistants = (state) => get(state, 'app.database.memberAssistants', {}); // Use lodash get
const getReservationDocuments = (state) => get(state, 'app.database.reservationDocuments', {}); // Use lodash get
const getExternalIdentifiers = (state) => get(state, 'app.database.externalIdentifiers', {}); // Use lodash get
const getReservationRelations = (state) => get(state, 'app.database.reservationRelations', {}); // Use lodash get
const getTravelAgencies = (state) => get(state, 'app.database.travelAgencies', {}); // Use lodash get

/**
 * Selector Builders
 */
export const buildMetaReservationSelector = () => createSelector([getEntityId, getMetaEntities], (entityId, metaEntities) =>
{
    if (has(metaEntities, entityId))
    {
        return metaEntities[entityId];
    }

    return {};
});

export const buildReservationSelector = (componentStatuses = []) => createSelector([
    getEntityId,
    getEntities,
    getRequests,
    getUsers,
    getMembers,
    getTravelAgencies,
    getMemberEmails,
    getReservationAssistants,
    getReservationDocuments,
    getExternalIdentifiers,
    getReservationRelations,
    buildReservationComponentsByReservationIdSelector(),
], (entityId, entities, requests, users, members, travelAgencies, membersEmails, reservationsAssistants, reservationsDocuments, externalIdentifiers, reservationRelations, reservationComponents) =>
{
    if (has(entities, entityId))
    {
        const reservation = entities[entityId];
        const request = get(requests, reservation.request_id);
        const user = get(users, get(request, 'user_id'));
        const member = get(members, get(request, 'member_id'));
        const travelAgency = get(travelAgencies, get(member, 'travel_agency_id'));
        const memberEmails = filter(membersEmails, { member_id: get(request, 'member_id') });
        const reservationDocuments = filter(reservationsDocuments, { reservation_id: entityId });
        const reservationExternalIdentifiers = filter(externalIdentifiers, { local_type: 'Reservation', local_id: entityId });
        const requestExternalIdentifiers = filter(externalIdentifiers, { local_type: 'Request', local_id: reservation.request_id });
        const relations = filter(reservationRelations, (reservationRelation) => reservationRelation.reservation_id === entityId || reservationRelation.related_reservation_id === entityId);

        const reservationAssistants = values(reservationsAssistants).map((reservationsAssistant) =>
        {
            if (reservationsAssistant.reservation_id === entityId)
            {
                return { ...reservationsAssistant, assistant: find(members, { id: reservationsAssistant.member_assistant_id }) };
            }

            return null;
        }).filter((n) => n);

        const relatedReservations = relations.map((relation) =>
        {
            if (relation.reservation_id === entityId)
            {
                return entities[relation.related_reservation_id];
            }

            return entities[relation.reservation_id];
        });

        const componentsFilters = component => componentStatuses.length ? componentStatuses.includes(component.status) : true;

        return {
            ...reservation,
            externalIdentifiers: reservationExternalIdentifiers,
            reservationDocuments,
            reservationAssistants,
            relatedReservations,
            reservationHotels: reservationComponents.reservationHotels.filter(componentsFilters),
            reservationFlights: reservationComponents.reservationFlights.filter(componentsFilters),
            reservationServices: reservationComponents.reservationServices.filter(componentsFilters),
            reservationInsurances: reservationComponents.reservationInsurances.filter(componentsFilters),
            request: {
                ...request,
                externalIdentifiers: requestExternalIdentifiers,
                member: { ...member, memberEmails, travelAgency },
                user: { ...user },
            },
        };
    }

    return null;
});

export const buildReservationComponentsByReservationIdSelector = (callback) => createSelector([
    getEntityId,
    buildReservationCruisesByReservationIdSelector(),
    buildReservationFlightsByReservationIdSelector(),
    buildReservationHotelsByReservationIdSelector(),
    buildReservationInsurancesByReservationIdSelector(),
    buildReservationServicesByReservationIdSelector(),
], (reservationId, reservationCruises, reservationFlights, reservationHotels, reservationInsurances, reservationServices) =>
{
    const components = {
        reservationCruises, reservationHotels, reservationFlights, reservationInsurances, reservationServices,
    };

    if (isFunction(callback)) return callback(components);

    return components;
});

export const buildReservationComponentsByReservationIdNoInformativeSelector = () => createSelector([buildReservationComponentsByReservationIdSelector()], (reservationComponents) => ({
    ...reservationComponents,
    reservationFlights: reservationComponents.reservationFlights.filter((f) => f.type !== 'Informative'),
    reservationServices: reservationComponents.reservationServices.filter((f) => f.type !== 'Informative'),
}));

export const buildReservationDatesSelector = () => createSelector([
    getEntityId,
    buildReservationComponentsByReservationIdSelector(),
    buildReservationHotelRoomsByReservationId(),
], (reservationId, reservationComponents, rooms) => ({
    minCheckin: get(minBy(rooms, 'checkin'), 'checkin'),
    maxCheckin: get(maxBy(rooms, 'checkin'), 'checkin'),
    minCheckout: get(minBy(rooms, 'checkout'), 'checkout'),
    maxCheckout: get(maxBy(rooms, 'checkout'), 'checkout'),
    minClientPaymentDueDate: get(minBy(flatMap(reservationComponents), 'client_payment_due_date'), 'client_payment_due_date'),
    minClientPaymentDueDateByHotel: get(minBy(reservationComponents.reservationHotels, 'client_payment_due_date'), 'client_payment_due_date'),
}));
